import {
  Box,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  ToggleButtonGroup,
  ToggleButton,
  Switch,
  Typography,
} from "@mui/material";
import {
  Add,
  Assignment,
  Flag,
  Folder,
  UnfoldLess,
  UnfoldMore,
  Timeline,
  Timelapse,
} from "@mui/icons-material";
import { useParams } from "react-router-dom";
import { FormDrawer } from "../../../shared/components";
import { Task } from "@dhx/react-gantt";
import { ProjectGantt } from "../components/ProjectGantt";
import {
  PhaseForm,
  PhaseFormActions,
  ActivityForm,
  ActivityFormActions,
  StageGateForm,
  StageGateFormActions
} from "../components";
import { useScheduleEditor } from "../hooks";
import { useCapabilities } from "../../../hooks/useCapabilities";
import { useAssetDetails } from "../../projectOverview/hooks/useAssetDetails";
import { useState } from "react";
import { OutageWindowForm } from "../components/OutageWindowForm";

export const ScheduleAndPlanning: React.FC = () => {

  const {
    isAdminPerson, isPDEPerson, isDataControllerPerson,
    isAllowedToAddActivity, isAllowedToDeleteActivity,
    isAllowedToAddPhase, isAllowedToDeletePhase,
    isAllowedToAddStageGate, isAllowedToDeleteStageGate,
    isAllowedToAddOutageWindow
  } = useCapabilities();

  const { projectId } = useParams<{ projectId: string }>();
  const { assetData } = useAssetDetails(projectId);

  const {
    stageGateId, isLoading, isError, error, isCreating, isUpdating, isDeleting, openDrawer, editingScheduleItem,
    deleteConfirmOpen, phaseFormData, activityFormData, stageGateFormData, tasks, ganttLinks, anchorEl, zoomLevel,
    isCollapsed, phaseDisabledConfig, activityDisabledConfig, stageGateDisabledConfig, phaseOptions, phaseOptionsActivity,
    getPhaseOptionsForEdit, isStageGateCompleted, areAllActivitiesCompleted, hasLinkedStageGate, isLinkedStageGateCompleted,
    isEstimateValidationStageGateCompleted, isCommercialFocusActivity, isCommercialFocusPhase, showCriticalPath, ganttRef, showSlack, phaseFormRef, activityFormRef, stageGateFormRef, outageWindows,
    handleSaveActivity, handleSaveStageGate, handleLinkAdd, handleLinkDelete, handleTaskReorder, handleDeleteRequest, setShowSlack,
    handleTaskUpdate, handleProgressUpdate, handleAddClick, handleMenuClose, handleMenuItemClickWrapper, handleZoomChange, handleToggleCollapse,
    handleDeleteConfirm, handleDeleteCancel, handleSavePhase, handleDrawerClose, handleTaskDblClick, handleAddTaskFromGantt, setShowCriticalPath
  } = useScheduleEditor(Number(projectId));

  const isActivityLocked = isStageGateCompleted && activityFormData.progress === 100 && activityFormData.phase === "Estimate/Validation";
  const isStageGateLocked = isStageGateCompleted && stageGateFormData.parentPhase === "Estimate/Validation";
  const isPhaseLocked = isStageGateCompleted && phaseFormData.progress === 100 && phaseFormData.phase === "Estimate/Validation";

  const canCommercialActivityDeleted = !isActivityLocked && Boolean(isAllowedToDeleteActivity);
  const canCommercialStageGateDeleted = !isStageGateLocked && Boolean(isAllowedToDeleteStageGate);
  const canCommercialPhaseDeleted = !isPhaseLocked && Boolean(isAllowedToDeletePhase);

  const [dragWarningQueue, setDragWarningQueue] = useState<string[]>([]);
  const [dragWarningTitle, setDragWarningTitle] = useState("");
  const [pendingDragTask, setPendingDragTask] = useState<{ task: Task; mode: string } | null>(null);

  const currentDragWarning = dragWarningQueue[0] ?? null;

  const handleTaskDragWarning = (task: Task, mode: string, warnings: string[], title: string) => {
    setPendingDragTask({ task, mode });
    setDragWarningQueue(warnings);
    setDragWarningTitle(title);
  };

  const handleDragWarningIgnore = () => {
    const remaining = dragWarningQueue.slice(1);
    setDragWarningQueue(remaining);
    if (remaining.length === 0 && pendingDragTask) {
      handleTaskUpdate(pendingDragTask.task);
      setPendingDragTask(null);
    }
  };

  const handleDragWarningCancel = () => {
    setDragWarningQueue([]);
    setPendingDragTask(null);
    ganttRef.current?.revertTasks(tasks, ganttLinks);
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">
          Failed to load scheduling data.{" "}
          {error || "Please try again."}
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: "100%",
        width: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          p: 2,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <ToggleButtonGroup
          value={zoomLevel}
          exclusive
          onChange={handleZoomChange}
          aria-label="zoom level"
          size="small"
        >
          <ToggleButton value="day" aria-label="day view">
            Day
          </ToggleButton>
          <ToggleButton value="week" aria-label="week view">
            Week
          </ToggleButton>
          <ToggleButton value="month" aria-label="month view">
            Month
          </ToggleButton>
          <ToggleButton value="year" aria-label="year view">
            Year
          </ToggleButton>
        </ToggleButtonGroup>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              border: 1,
              borderColor: showCriticalPath ? "#e63900" : "divider",
              borderRadius: 1,
              px: 1.5,
              py: 0.25,
              transition: "border-color 0.2s",
            }}
          >
            <Timeline sx={{ fontSize: 18, color: showCriticalPath ? "#e63900" : "action.disabled" }} />
            <Typography variant="body2" color={showCriticalPath ? "text.primary" : "text.secondary"}>
              Critical Path
            </Typography>
            <Switch
              checked={showCriticalPath}
              onChange={(_, checked) => {
                setShowCriticalPath(checked);
                ganttRef.current?.toggleCriticalPath(checked);
              }}
              size="small"
              sx={{
                "& .MuiSwitch-switchBase.Mui-checked": { color: "#e63900" },
                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: "#e63900" },
              }}
            />
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              border: 1,
              borderColor: showSlack ? "#757575" : "divider",
              borderRadius: 1,
              px: 1.5,
              py: 0.25,
              transition: "border-color 0.2s",
            }}
          >
            <Timelapse sx={{ fontSize: 18, color: showSlack ? "#757575" : "action.disabled" }} />
            <Typography variant="body2" color={showSlack ? "text.primary" : "text.secondary"}>
              Slack
            </Typography>
            <Switch
              checked={showSlack}
              onChange={(_, checked) => {
                setShowSlack(checked);
                ganttRef.current?.toggleSlack(checked);
              }}
              size="small"
            />
          </Box>
          <Box sx={{ height: 24, borderLeft: 1, borderColor: "divider" }} />
          <Button
            variant="text"
            size="small"
            startIcon={isCollapsed ? <UnfoldMore /> : <UnfoldLess />}
            onClick={handleToggleCollapse}
            sx={{
              color: "text.secondary",
              textTransform: "none",
              fontWeight: 500,
              fontSize: "0.8125rem",
            }}
          >
            {isCollapsed ? "Expand All" : "Collapse All"}
          </Button>
          {
            (isAllowedToAddActivity || isAllowedToAddPhase || isAllowedToAddStageGate || isAllowedToAddOutageWindow ) && (
              <Button
                variant="text"
                startIcon={<Add />}
                onClick={handleAddClick}
                disabled={isCreating || isUpdating || isDeleting}
                sx={{
                  color: "primary.main",
                  textTransform: "uppercase",
                  fontWeight: 600,
                }}
              >
                Add
              </Button>
            )
          }
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
          >
            {
              isAllowedToAddActivity && (
                <MenuItem onClick={() => handleMenuItemClickWrapper("activity")}>
                  <ListItemIcon>
                    <Assignment fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Add Activity</ListItemText>
                </MenuItem>
              )
            }
            {
              isAllowedToAddStageGate && (
                <MenuItem onClick={() => handleMenuItemClickWrapper("stageGate")}>
                  <ListItemIcon>
                    <Flag fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Add Stage Gate</ListItemText>
                </MenuItem>
              )
            }
            {
              isAllowedToAddPhase && (
                <MenuItem onClick={() => handleMenuItemClickWrapper("phase")}>
                  <ListItemIcon>
                    <Folder fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Add Phase</ListItemText>
                </MenuItem>
              )
            }
            {
              isAllowedToAddOutageWindow && (
                <MenuItem onClick={() => handleMenuItemClickWrapper("outageWindow")}>
                  <ListItemIcon>
                    <Timelapse fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Add Outage Window</ListItemText>
                </MenuItem>
              )
            }
          </Menu>
        </Box>
      </Box>

      <Box sx={{ flex: 1, overflow: "hidden" }}>
        <ProjectGantt
          ref={ganttRef}
          tasks={tasks}
          links={ganttLinks}
          outageWindows={outageWindows}
          isReadOnly={!(isAdminPerson || isPDEPerson || isDataControllerPerson)}
          onTaskDblClick={handleTaskDblClick}
          onAddTask={handleAddTaskFromGantt}
          onTaskUpdate={handleTaskUpdate}
          onProgressUpdate={handleProgressUpdate}
          onTaskReorder={handleTaskReorder}
          onLinkAdd={handleLinkAdd}
          onLinkDelete={handleLinkDelete}
          onTaskDragWarning={handleTaskDragWarning}
        />
      </Box>

      <FormDrawer
        open={openDrawer === "phase"}
        onClose={handleDrawerClose}
        title={editingScheduleItem ? "Edit Phase" : "Add Phase"}
        actions={
          <PhaseFormActions
            onSave={() => phaseFormRef.current?.submit()}
            onCancel={handleDrawerClose}
            onDelete={handleDeleteRequest}
            isEditing={!!editingScheduleItem}
            isDeleteAllowed={canCommercialPhaseDeleted}
            isCommercialFocusPhase={isCommercialFocusPhase}
            isEstimateValidationStageGateCompleted={isEstimateValidationStageGateCompleted}
          />
        }
      >
        <PhaseForm
          ref={phaseFormRef}
          onSave={handleSavePhase}
          onCancel={handleDrawerClose}
          initialData={phaseFormData}
          isEditing={!!editingScheduleItem}
          projectId={projectId}
          phaseDisabledConfig={phaseDisabledConfig}
          isStageGateCompleted={isStageGateCompleted}
          areAllActivitiesCompleted={areAllActivitiesCompleted}
          hasLinkedStageGate={hasLinkedStageGate}
          isLinkedStageGateCompleted={isLinkedStageGateCompleted}
          outageWindows={outageWindows}
        />
      </FormDrawer>

      <FormDrawer
        open={openDrawer === "activity"}
        onClose={handleDrawerClose}
        title={editingScheduleItem ? "Edit Activity" : "Add Activity"}
        actions={
          <ActivityFormActions
            onSave={() => activityFormRef.current?.submit()}
            onCancel={handleDrawerClose}
            onDelete={handleDeleteRequest}
            isEditing={!!editingScheduleItem}
            isDeleteAllowed={canCommercialActivityDeleted}
            isEstimateValidationStageGateCompleted={isEstimateValidationStageGateCompleted}
            isCommercialFocusActivity={isCommercialFocusActivity}
          />
        }
      >
        <ActivityForm
          isEditing={!!editingScheduleItem}
          ref={activityFormRef}
          onSave={handleSaveActivity}
          onCancel={handleDrawerClose}
          initialData={activityFormData}
          phaseOptions={phaseOptionsActivity}
          stageGateId={stageGateId}
          activityId={editingScheduleItem?.id}
          assetData={assetData || []}
          projectId={projectId}
          activityDisabledConfig={activityDisabledConfig}
          isStageGateCompleted={isStageGateCompleted}
          isEstimateValidationStageGateCompleted={isEstimateValidationStageGateCompleted}
        />

      </FormDrawer>

      <FormDrawer
        open={openDrawer === "stageGate"}
        onClose={handleDrawerClose}
        title={editingScheduleItem || stageGateId ? "Edit Stage Gate" : "Add Stage Gate"}
        actions={
          <StageGateFormActions
            onSave={() => stageGateFormRef.current?.submit()}
            onCancel={handleDrawerClose}
            onDelete={handleDeleteRequest}
            isEditing={!!stageGateId}
            isDeleteAllowed={canCommercialStageGateDeleted}
          />
        }
      >
        <StageGateForm
          ref={stageGateFormRef}
          stageGateId={stageGateId}
          onSave={handleSaveStageGate}
          onCancel={handleDrawerClose}
          initialData={stageGateFormData}
          isEditing={!!editingScheduleItem}
          phaseOptions={editingScheduleItem ? getPhaseOptionsForEdit : phaseOptions}
          projectId={projectId}
          stageGateDisabledConfig={stageGateDisabledConfig}
          isStageGateCompleted={isStageGateCompleted}
        />
      </FormDrawer>

      <FormDrawer
        open={openDrawer === "outageWindow"}
        onClose={handleDrawerClose}
        title={"Add / Edit Outage Window"}
      >
        <OutageWindowForm
          projectId={projectId}
        />
      </FormDrawer>

      <Dialog
        open={deleteConfirmOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete "{editingScheduleItem?.name}"?
            {editingScheduleItem?.type === "phase" && (
              <Box
                component="span"
                sx={{ display: "block", mt: 1, fontWeight: "bold" }}
              >
                Warning: Deleting this phase may affect associated activities.
              </Box>
            )}
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="primary"
            variant="contained"
            autoFocus
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!currentDragWarning} onClose={handleDragWarningCancel}>
        <DialogTitle>{dragWarningTitle}</DialogTitle>
      <DialogContent>
        <DialogContentText>{currentDragWarning}</DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleDragWarningCancel}>Cancel</Button>
        <Button onClick={handleDragWarningIgnore} autoFocus>
          {dragWarningQueue.length > 1 &&
           dragWarningQueue.indexOf(currentDragWarning!) < dragWarningQueue.length - 1
            ? "Ignore & Continue"
            : "Ignore & Save"}
        </Button>
      </DialogActions>
    </Dialog>

    </Box>
  );
};